// ===== Register.jsx (React) =====
import React, { useState } from 'react';
import {
    Button,
    Form,
    Input,
    InputNumber,
    message,
    Card,
    Typography,
    Row,
    Col,
    Divider,
    Space,
    Alert,
} from 'antd';
import { registerHeadler } from '../../../functions/auth';
import { useNavigate, Link } from 'react-router-dom';
import styles from './styles/Register.module.css';

const { Title, Text } = Typography;

// Utils
const trimOrEmpty = (v) => (typeof v === 'string' ? v.trim() : v);

export default function Register() {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const [serverError, setServerError] = useState('');

    const onFinish = async (values) => {
        setServerError('');

        const payload = {
            email: trimOrEmpty(values.email),
            password: trimOrEmpty(values.password),
            village: trimOrEmpty(values.village),
            phase: parseInt(values.phase, 10),
            subdistrict: trimOrEmpty(values.subdistrict),
            district: trimOrEmpty(values.district),
            province: trimOrEmpty(values.province),
        };

        if (!payload.email || !payload.password || !payload.village || !payload.phase) {
            message.error('กรุณากรอกข้อมูลให้ครบถ้วน');
            return;
        }

        setSubmitting(true);
        try {
            const res = await registerHeadler(payload);

            if (res && (res.status === 201 || res.status === 200)) {
                message.success({
                    content: 'ลงทะเบียนสำเร็จ! กำลังนำคุณไปยังหน้าเข้าสู่ระบบ...',
                    duration: 1.6,
                });
                form.resetFields();
                setTimeout(() => navigate('/login'), 1200);
            } else {
                const msg = res?.data?.message || 'ไม่สามารถลงทะเบียนได้ โปรดลองใหม่';
                setServerError(msg);
            }
        } catch (err) {
            console.error('Registration failed:', err);
            const serverMsg =
                err?.response?.data?.message || err?.message || 'เกิดข้อผิดพลาดในการลงทะเบียน';
            setServerError(serverMsg);
        } finally {
            setSubmitting(false);
        }
    };

    const onFinishFailed = ({ errorFields }) => {
        if (errorFields?.length) {
            form.scrollToField(errorFields[0].name, { behavior: 'smooth' });
        }
    };

    return (
    <div className={`${styles.page} page-background page-centered`}>
            <Card className={styles.card} bordered={false}>
                <header className={styles.header}>
                    <img src="/logoTackXpert.png" alt="TackXpert Logo" className={styles.logo} />
                    <Title level={2} className="page-title">TRACXPERT</Title>
                    <Text type="secondary" className="page-subtitle">สร้างบัญชีใหม่สำหรับระบบจัดการทะเบียนรถหมู่บ้าน</Text>
                </header>

                <Divider className={styles.divider} />

                <Space direction="vertical" size={16} className={styles.stack}>
                    {serverError ? (
                        <Alert
                            type="error"
                            message="ลงทะเบียนไม่สำเร็จ"
                            description={serverError}
                            showIcon
                            className={styles.alert}
                        />
                    ) : null}

                    <Form
                        form={form}
                        name="register"
                        layout="vertical"
                        onFinish={onFinish}
                        onFinishFailed={onFinishFailed}
                        scrollToFirstError
                        size="large"
                        autoComplete="off"
                        disabled={submitting}
                        className={styles.form}
                        noValidate
                    >
                        <Row gutter={16}>
                            {/* กลุ่ม: บัญชีผู้ใช้ */}
                            <Col span={24}>
                                <Title level={5} className={styles.sectionTitle}>ข้อมูลบัญชีผู้ใช้</Title>
                            </Col>

                            <Col span={24}>
                                <Form.Item
                                    name="email"
                                    label="อีเมล"
                                    rules={[
                                        { type: 'email', message: 'กรุณากรอกอีเมลให้ถูกต้อง' },
                                        { required: true, message: 'กรุณากรอกอีเมล' },
                                        { pattern: /^\S+$/, message: 'ห้ามมีช่องว่างในอีเมล' },
                                    ]}
                                    normalize={(v) => trimOrEmpty(v)}
                                >
                                    <Input placeholder="example@email.com" maxLength={120} />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12}>
                                <Form.Item
                                    name="password"
                                    label="รหัสผ่าน"
                                    rules={[
                                        { required: true, message: 'กรุณากรอกรหัสผ่าน' },
                                        { min: 8, message: 'รหัสผ่านต้องยาวอย่างน้อย 8 ตัวอักษร' },
                                        { pattern: /^\S+$/, message: 'ห้ามมีช่องว่างในรหัสผ่าน' },
                                    ]}
                                    hasFeedback
                                    normalize={(v) => trimOrEmpty(v)}
                                >
                                    <Input.Password
                                        placeholder="อย่างน้อย 8 ตัว และผสม A-z, 0-9, อักขระพิเศษ"
                                        autoComplete="new-password"
                                    />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12}>
                                <Form.Item
                                    name="confirm"
                                    label="ยืนยันรหัสผ่าน"
                                    dependencies={['password']}
                                    hasFeedback
                                    rules={[
                                        { required: true, message: 'กรุณายืนยันรหัสผ่าน' },
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (!value || getFieldValue('password') === value) return Promise.resolve();
                                                return Promise.reject(new Error('รหัสผ่านไม่ตรงกัน'));
                                            },
                                        }),
                                    ]}
                                    normalize={(v) => trimOrEmpty(v)}
                                >
                                    <Input.Password autoComplete="new-password" placeholder="ยืนยันรหัสผ่าน" />
                                </Form.Item>
                            </Col>

                            {/* กลุ่ม: ข้อมูลหมู่บ้าน */}
                            <Col span={24}>
                                <Divider className={styles.dividerTight} />
                                <Title level={5} className={styles.sectionTitle}>ข้อมูลหมู่บ้าน</Title>
                            </Col>

                            <Col xs={24} sm={12}>
                                <Form.Item
                                    name="village"
                                    label="ชื่อหมู่บ้าน"
                                    rules={[
                                        { required: true, message: 'กรุณากรอกชื่อหมู่บ้าน' },
                                        { min: 2, message: 'อย่างน้อย 2 ตัวอักษร' },
                                    ]}
                                    normalize={(v) => trimOrEmpty(v)}
                                >
                                    <Input placeholder="เช่น ศิริกานต์ วิลล่า" maxLength={80} />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={12}>
                                <div className={styles.inputNumber}>
                                    <Form.Item
                                        label="เฟส"
                                        name="phase"
                                        rules={[
                                            { required: true, message: 'กรุณาระบุเฟสหมู่บ้าน' },
                                            { type: 'number', min: 1, message: 'ใส่เฉพาะตัวเลขที่มากกว่า 0' },
                                        ]}
                                    >
                                        <InputNumber
                                            min={1}
                                            max={99}
                                            placeholder="เช่น 1"
                                            controls={false}
                                            style={{ width: '100%' }}
                                        />
                                    </Form.Item>
                                </div>
                            </Col>

                            {/* กลุ่ม: ที่อยู่ */}
                            <Col span={24}>
                                <Divider className={styles.dividerTight} />
                                <Title level={5} className={styles.sectionTitle}>ที่อยู่</Title>
                            </Col>

                            <Col xs={24} sm={8}>
                                <Form.Item
                                    name="subdistrict"
                                    label="ตำบล"
                                    rules={[
                                        { required: true, message: 'กรุณากรอกตำบล' },
                                        { min: 2, message: 'อย่างน้อย 2 ตัวอักษร' },
                                    ]}
                                    normalize={(v) => trimOrEmpty(v)}
                                >
                                    <Input placeholder="ตำบล" />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={8}>
                                <Form.Item
                                    name="district"
                                    label="อำเภอ"
                                    rules={[
                                        { required: true, message: 'กรุณากรอกอำเภอ' },
                                        { min: 2, message: 'อย่างน้อย 2 ตัวอักษร' },
                                    ]}
                                    normalize={(v) => trimOrEmpty(v)}
                                >
                                    <Input placeholder="อำเภอ" />
                                </Form.Item>
                            </Col>

                            <Col xs={24} sm={8}>
                                <Form.Item
                                    name="province"
                                    label="จังหวัด"
                                    rules={[
                                        { required: true, message: 'กรุณากรอกจังหวัด' },
                                        { min: 2, message: 'อย่างน้อย 2 ตัวอักษร' },
                                    ]}
                                    normalize={(v) => trimOrEmpty(v)}
                                >
                                    <Input placeholder="จังหวัด" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider className={styles.divider} />

                        <Row gutter={16}>
                            <Col xs={24} sm={12}>
                                <Button
                                    block
                                    size="large"
                                    onClick={() => navigate('/login')}
                                    className={styles.outlineBtn}
                                    disabled={submitting}
                                >
                                    กลับไปหน้าเข้าสู่ระบบ
                                </Button>
                            </Col>
                            <Col xs={24} sm={12}>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    block
                                    size="large"
                                    loading={submitting}
                                    disabled={submitting}
                                    className={styles.primaryButton}
                                >
                                    {submitting ? 'กำลังลงทะเบียน…' : 'ลงทะเบียน'}
                                </Button>
                            </Col>

                        </Row>

                        <div className={styles.loginLinkWrap}>
                            <Text>
                                มีบัญชีอยู่แล้ว? <Link to="/login">เข้าสู่ระบบที่นี่</Link>
                            </Text>
                        </div>
                    </Form>
                </Space>
            </Card>

            <footer className="page-footer">
                <span>© {new Date().getFullYear()} TRACXPERT</span>
                <span className="dot">•</span>
                <a href="/help" rel="noreferrer">ช่วยเหลือ</a>
            </footer>
        </div>
    );
}
